import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EasProvisionRequest, EasWebhookRequest } from '@hangar/shared';
import { ApiError } from './client';
import { getEasSetup, provisionEas, registerEasWebhook } from './endpointsEas';

const easSetupKey = (appId: string) => ['eas-setup', appId] as const;

/** The app's EAS link (`GET /apps/{id}/eas`). A 404/501 (an API without the route) is not retried. */
export const useEasSetup = (appId: string | undefined, enabled = true) =>
  useQuery({
    queryKey: easSetupKey(appId ?? ''),
    queryFn: () => getEasSetup(appId ?? ''),
    enabled: Boolean(appId) && enabled,
    retry: (count, err) => count < 1 && !(err instanceof ApiError && err.status < 500),
  });

/** `POST /apps/{id}/eas/provision`: refreshes the link and the app lists (the app gets its `easProjectId`). */
export function useProvisionEas() {
  const qc = useQueryClient();
  const refresh = (appId: string) => {
    void qc.invalidateQueries({ queryKey: easSetupKey(appId) });
    void qc.invalidateQueries({ queryKey: ['apps'] });
  };
  return useMutation({
    mutationFn: (v: { appId: string; body?: EasProvisionRequest }) => provisionEas(v.appId, v.body),
    onSuccess: (_setup, v) => refresh(v.appId),
    onError: (_err, v) => refresh(v.appId),
  });
}

/** `POST /apps/{id}/eas/webhook`: registers or repairs the webhook and refreshes the link (the answer is the new status). */
export function useRegisterEasWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { appId: string; body?: EasWebhookRequest }) => registerEasWebhook(v.appId, v.body),
    onSettled: (_setup, _err, v) => {
      void qc.invalidateQueries({ queryKey: easSetupKey(v.appId) });
    },
  });
}
